import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Box,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { User } from '../../types';
import userService from '../../services/userService';

interface UsuariosSelectProps {
  value?: number | null;
  onChange: (value: number | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  allowClear?: boolean;
  sx?: any;
}

const UsuariosSelect: React.FC<UsuariosSelectProps> = ({
  value = null,
  onChange,
  label = 'Usuário',
  placeholder = 'Selecione o usuário',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  allowClear = true,
  sx,
}) => {
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers({ limit: 1000 });
      setUsuarios(response.users || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const v = event.target.value;
    onChange(v === '' ? null : Number(v));
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      disabled={disabled}
      required={required}
      variant="outlined"
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ?? ''}
        onChange={handleChange}
        label={label}
        sx={sx}
        renderValue={(selected: number | string) => {
          if (selected === '' || selected == null) {
            return <Box component="span" sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
          }
          const u = usuarios.find((x) => x.id === selected);
          return u ? `${u.nome} (${u.usuario})` : String(selected);
        }}
      >
        {allowClear && (
          <MenuItem value="">
            <em>Não informado</em>
          </MenuItem>
        )}
        {loading ? (
          <MenuItem value="" disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Carregando...
            </Box>
          </MenuItem>
        ) : (
          usuarios.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                {u.nome}
                <Box component="span" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  ({u.usuario}) - {u.perfil}
                </Box>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default UsuariosSelect;
